import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Input, Label, Modal } from '../../../shared/components';
import { 
  Save,
  Loader2
} from 'lucide-react';
import { useUserManagement } from '../../../shared/hooks';

/**
 * Formulario de usuario optimizado para modal
 * Maneja creación y edición de usuarios
 * Adaptado a los campos del CreateUserDto del backend
 */
const UserFormModal = ({ 
  isOpen, 
  onClose, 
  user = null, 
  mode = 'create',
  onSuccess,
  createUser,
  updateUser
}) => {
  const { loading, error, clearError } = useUserManagement();

  // Estados del formulario - adaptado al DTO del backend
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    lastName: '',
    password: '',
    role: 'user',
    sede: '',
    isActive: true,
  });

  // Estados de validación
  const [errors, setErrors] = useState({});

  // Estado inicial del formulario
  const initialFormData = useMemo(() => ({
    username: '',
    name: '',
    lastName: '',
    password: '',
    role: 'user',
    sede: '',
    isActive: true,
  }), []);

  // Función para limpiar el formulario
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
    clearError();
  }, [clearError, initialFormData]);

  // Inicializar formulario con datos del usuario si está en modo edición
  useEffect(() => {
    if (user && mode === 'edit') {
      setFormData({
        username: user.username || '',
        name: user.name || '',
        lastName: user.lastName || '',
        password: '', // No mostrar contraseña actual
        role: user.role || 'user',
        sede: user.sede || '',
        isActive: user.isActive !== undefined ? user.isActive : true,
      });
    } else {
      // Resetear formulario para creación
      resetForm();
    }
  }, [user, mode, clearError, resetForm]);

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};

    // Validar username
    if (!formData.username.trim()) {
      newErrors.username = 'El username es requerido';
    } else if (formData.username.length < 3) {
      newErrors.username = 'El username debe tener al menos 3 caracteres';
    }

    // Validar name
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    // Validar lastName
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'El apellido es requerido';
    }

    // Validar contraseña (solo para crear o si se está cambiando)
    if (mode === 'create' || formData.password) {
      if (!formData.password) {
        newErrors.password = 'La contraseña es requerida';
      } else if (formData.password.length < 6) {
        newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
      }
    }

    // Validar sede (obligatorio solo para usuarios, null para admins)
    if (formData.role === 'user') {
      if (!formData.sede || formData.sede.trim() === '') {
        newErrors.sede = 'La sede es obligatoria para usuarios';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar cambios en el formulario
  const handleInputChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    
    // Si se cambia el rol a admin, limpiar sede
    if (field === 'role' && value === 'admin') {
      newData.sede = '';
    }
    
    setFormData(newData);

    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }

    // Limpiar error de sede cuando se cambia a admin
    if (field === 'role' && value === 'admin' && errors.sede) {
      setErrors(prev => ({
        ...prev,
        sede: null
      }));
    }
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Limpiar errores previos
    setErrors({});
    
    if (!validateForm()) {
      return;
    }

    try {
      let result;
      
      // Preparar datos para envío
      const submitData = { ...formData };
      
      // Si es admin, asegurar que sede sea null
      if (submitData.role === 'admin') {
        submitData.sede = null;
      }
      
      if (mode === 'create') {
        result = await createUser(submitData);
      } else {
        // Solo enviar contraseña si se está cambiando
        const updateData = { ...submitData };
        if (!updateData.password) {
          delete updateData.password;
        }
        result = await updateUser(user.id, updateData);
      }

      if (result) {
        onSuccess?.(result, mode);
        resetForm();
        onClose();
      }
    } catch (err) {
      // Manejar errores específicos por campo
      const errorMessage = err.message || 'Error desconocido';
      
      // Detectar errores específicos de duplicación
      if (errorMessage.includes('username') && errorMessage.includes('ya está en uso')) {
        setErrors(prev => ({
          ...prev,
          username: 'Este nombre de usuario ya está en uso. Por favor, elige otro.'
        }));
      } else if (errorMessage.includes('email') && errorMessage.includes('ya está registrado')) {
        setErrors(prev => ({
          ...prev,
          email: 'Este correo electrónico ya está registrado. Por favor, usa otro.'
        }));
      } else {
        // Error general
        setErrors(prev => ({
          ...prev,
          general: errorMessage
        }));
      }
      
      // NO cerrar el modal cuando hay errores
      // El usuario debe poder corregir los errores
    }
  };

  // Manejar cierre del modal
  const handleClose = () => {
    // Solo permitir cerrar si no está cargando
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === 'create' ? 'Crear Nuevo Usuario' : 'Editar Usuario'}
      size="lg"
      closeOnOverlayClick={!loading}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Nombre */}
        <div>
          <Label htmlFor="name">Nombre *</Label>
          <Input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Ingresa el nombre"
            className={`${errors.name ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
            disabled={loading}
          />
          {errors.name && (
            <div className="flex items-center mt-1">
              <svg className="h-4 w-4 text-red-500 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-red-500 text-sm">{errors.name}</p>
            </div>
          )}
        </div>

        {/* Apellido */}
        <div>
          <Label htmlFor="lastName">Apellido *</Label>
          <Input
            id="lastName"
            type="text"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            placeholder="Ingresa el apellido"
            className={`${errors.lastName ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
            disabled={loading}
          />
          {errors.lastName && (
            <div className="flex items-center mt-1">
              <svg className="h-4 w-4 text-red-500 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-red-500 text-sm">{errors.lastName}</p>
            </div>
          )}
        </div>

        {/* Username */}
        <div>
          <Label htmlFor="username">Username *</Label>
          <Input
            id="username"
            type="text"
            value={formData.username}
            onChange={(e) => handleInputChange('username', e.target.value)}
            placeholder="Ingresa el username"
            className={`${errors.username ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
            disabled={mode === 'edit' || loading} // No permitir cambiar username en edición
          />
          {errors.username && (
            <div className="flex items-center mt-1">
              <svg className="h-4 w-4 text-red-500 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-red-500 text-sm">{errors.username}</p>
            </div>
          )}
        </div>

        {/* Contraseña */}
        <div>
          <Label htmlFor="password">
            Contraseña {mode === 'edit' ? '(dejar vacío para no cambiar)' : '*'}
          </Label>
          <Input
            id="password"
            type="password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            placeholder={mode === 'create' ? 'Ingresa la contraseña' : 'Nueva contraseña (opcional)'}
            className={`${errors.password ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
            disabled={loading}
          />
          {errors.password && (
            <div className="flex items-center mt-1">
              <svg className="h-4 w-4 text-red-500 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-red-500 text-sm">{errors.password}</p>
            </div>
          )}
        </div>

        {/* Rol */}
        <div>
          <Label htmlFor="role">Rol *</Label>
          <select
            id="role"
            value={formData.role}
            onChange={(e) => handleInputChange('role', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          >
            <option value="user">Usuario</option>
            <option value="admin">Administrador</option>
          </select>
        </div>

        {/* Sede - Solo mostrar para usuarios */}
        {formData.role === 'user' && (
          <div>
            <Label htmlFor="sede">Sede *</Label>
            <select
              id="sede"
              value={formData.sede}
              onChange={(e) => handleInputChange('sede', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.sede ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
              }`}
              disabled={loading}
            >
              <option value="">Seleccionar sede</option>
              <option value="Belgrano">Belgrano</option>
              <option value="Caballito">Caballito</option>
              <option value="Palermo">Palermo</option>
            </select>
            {errors.sede && (
              <div className="flex items-center mt-1">
                <svg className="h-4 w-4 text-red-500 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-red-500 text-sm">{errors.sede}</p>
              </div>
            )}
          </div>
        )}

        {/* Estado activo */}
        <div>
          <Label htmlFor="isActive">Estado</Label>
          <div className="flex items-center mt-2">
            <input
              id="isActive"
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => handleInputChange('isActive', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled={loading}
            />
            <Label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
              Usuario activo
            </Label>
          </div>
        </div>

        {/* Error general */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-300 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error del sistema</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error general del formulario */}
        {errors.general && (
          <div className="p-4 bg-red-50 border border-red-300 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error de validación</h3>
                <p className="text-sm text-red-700 mt-1">{errors.general}</p>
              </div>
            </div>
          </div>
        )}

        {/* Indicador de errores en campos */}
        {(Object.keys(errors).length > 0 && !errors.general && !error) && (
          <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-yellow-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-yellow-800">Por favor, corrige los errores en los campos marcados</span>
            </div>
          </div>
        )}

        {/* Botones */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            onClick={handleClose}
            variant="outline"
            disabled={loading}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {mode === 'create' ? 'Crear Usuario' : 'Guardar Cambios'}
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UserFormModal; 